/**
 * Settings → Tracking: types and calls for api/src/routes/tracking.ts
 * (health numbers, conversion destinations, the send log) and the
 * plain-English names for the ad platforms behind them.
 */
import { api, ApiError } from "@/lib/api";

export interface TrackingHealth {
  pageviews_30d: number;
  visitors_30d: number;
  linked_visitors: number;
  contacts_with_history: number;
  touches: number;
  touches_with_click_ids: number;
  credited: number;
  uncredited: number;
}

export type Outcome = "signup" | "trial" | "purchase";

export interface Destination {
  id: string;
  platform: string;
  account_id: string;
  destination_id: string;
  name: string | null;
  enabled: boolean;
  events: Partial<Record<Outcome, boolean>>;
  test_code: string | null;
  created_at: string;
  updated_at: string;
  sent: number;
  failed: number;
  last_send_at: string | null;
}

export interface Send {
  id: string | number;
  destination_id: string | null;
  event: Outcome | string;
  event_id: string;
  contact_id: string | null;
  account_id: string | null;
  value_usd: string | number | null;
  /** sent | failed | skipped | sending */
  status: string;
  detail: string | null;
  matched_on: string[] | null;
  created_at: string;
  platform: string | null;
  destination_name: string | null;
  contact_name?: string | null;
}

export interface TrackingData {
  health: TrackingHealth;
  destinations: Destination[];
  sends: Send[];
  configured: boolean;
}

export interface DiscoveredAccount {
  accountId: string;
  platform: string;
  name: string | null;
  destinations: Array<{ id: string; name: string; status: string | null }>;
  error?: string;
}

export const trackingApi = {
  load: () => api.get<TrackingData>("/tracking"),
  discover: () => api.get<{ accounts: DiscoveredAccount[]; error?: string }>("/tracking/discover"),
  /** Insert or update (keyed by account + destination on the server). */
  save: (body: {
    platform: string;
    account_id: string;
    destination_id: string;
    name?: string | null;
    enabled?: boolean;
    events?: Record<Outcome, boolean>;
    test_code?: string | null;
  }) => api.put<{ destination: Destination }>("/tracking/destinations", body),
  remove: (id: string) => api.delete<void>(`/tracking/destinations/${id}`),
  test: (id: string) => api.post<{ send: Send | null }>(`/tracking/destinations/${id}/test`, {}),
};

export function errText(err: unknown, fallback = "Something went wrong."): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : fallback;
}

/** "metaads" → Meta, with the word each platform uses for a destination. */
const PLATFORMS: Record<string, { label: string; noun: string; dot: string }> = {
  metaads: { label: "Meta", noun: "Pixel / dataset", dot: "blue" },
  facebookads: { label: "Meta", noun: "Pixel / dataset", dot: "blue" },
  googleads: { label: "Google Ads", noun: "Conversion action", dot: "amber" },
  tiktokads: { label: "TikTok", noun: "Event set", dot: "fuchsia" },
  linkedinads: { label: "LinkedIn", noun: "Conversion", dot: "sky" },
  pinterestads: { label: "Pinterest", noun: "Conversion tag", dot: "rose" },
  xads: { label: "X", noun: "Pixel", dot: "zinc" },
  twitterads: { label: "X", noun: "Pixel", dot: "zinc" },
  snapchatads: { label: "Snapchat", noun: "Pixel", dot: "yellow" },
  redditads: { label: "Reddit", noun: "Pixel", dot: "orange" },
  microsoftads: { label: "Microsoft Ads", noun: "Conversion goal", dot: "teal" },
};

export function platformInfo(platform: string | null | undefined): { label: string; noun: string; dot: string } {
  const key = String(platform ?? "").toLowerCase();
  if (PLATFORMS[key]) return PLATFORMS[key]!;
  const base = key.replace(/ads$/, "");
  return { label: base ? base.charAt(0).toUpperCase() + base.slice(1) : "Ads", noun: "Destination", dot: "indigo" };
}

/** The server's send details name platforms by id ("Accepted by metaads"); say it in words. */
export function plainDetail(detail: string | null | undefined): string {
  const ids = new RegExp(`\\b(${Object.keys(PLATFORMS).join("|")})\\b`, "gi");
  return String(detail ?? "").replace(ids, (m) => platformInfo(m).label);
}

export const OUTCOMES: Array<{ key: Outcome; label: string; short: string }> = [
  { key: "signup", label: "Signups", short: "Signup" },
  { key: "trial", label: "Trial starts", short: "Trial" },
  { key: "purchase", label: "Payments", short: "Payment" },
];

export function outcomeLabel(event: string): string {
  return OUTCOMES.find((o) => o.key === event)?.short ?? event;
}

/** Events with the server's defaults filled in (anything missing counts as on). */
export function eventsOf(d: Destination): Record<Outcome, boolean> {
  return { signup: d.events?.signup !== false, trial: d.events?.trial !== false, purchase: d.events?.purchase !== false };
}
